import { useEffect } from 'react';
import { useOrgStore } from '@/stores/org-store';

/**
 * Determines if a color is light or dark to calculate appropriate foreground color
 */
const isLightColor = (hexColor: string): boolean => {
  // Validate hex color format
  if (!/^#?[0-9A-Fa-f]{6}$/.test(hexColor)) {
    return false; // Treat invalid colors as dark
  }

  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Defensive check in case parsing fails
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return false;
  }

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

/**
 * Component that applies organization branding colors as CSS variables
 * This allows the primary color to be used throughout the app
 */
export const BrandingStyles = () => {
  const { currentOrg } = useOrgStore();

  useEffect(() => {
    const primaryColor = currentOrg?.branding?.primary_color;

    if (primaryColor) {
      // Determine appropriate foreground color based on background brightness
      const foregroundColor = isLightColor(primaryColor)
        ? '#000000'
        : '#ffffff';

      // Apply the branding color to CSS variables
      document.documentElement.style.setProperty('--primary', primaryColor);
      document.documentElement.style.setProperty(
        '--color-primary',
        primaryColor,
      );
      document.documentElement.style.setProperty(
        '--primary-foreground',
        foregroundColor,
      );
      document.documentElement.style.setProperty('--ring', primaryColor);
      document.documentElement.style.setProperty(
        '--sidebar-primary',
        primaryColor,
      );
      document.documentElement.style.setProperty(
        '--sidebar-primary-foreground',
        foregroundColor,
      );
      document.documentElement.style.setProperty(
        '--sidebar-ring',
        primaryColor,
      );
    } else {
      // Reset to default colors if no branding color is set
      document.documentElement.style.removeProperty('--primary');
      document.documentElement.style.removeProperty('--color-primary');
      document.documentElement.style.removeProperty('--primary-foreground');
      document.documentElement.style.removeProperty('--ring');
      document.documentElement.style.removeProperty('--sidebar-primary');
      document.documentElement.style.removeProperty(
        '--sidebar-primary-foreground',
      );
      document.documentElement.style.removeProperty('--sidebar-ring');
    }

    // Cleanup function to reset on unmount or org change
    return () => {
      document.documentElement.style.removeProperty('--primary');
      document.documentElement.style.removeProperty('--color-primary');
      document.documentElement.style.removeProperty('--primary-foreground');
      document.documentElement.style.removeProperty('--ring');
      document.documentElement.style.removeProperty('--sidebar-primary');
      document.documentElement.style.removeProperty(
        '--sidebar-primary-foreground',
      );
      document.documentElement.style.removeProperty('--sidebar-ring');
    };
  }, [currentOrg?.branding?.primary_color]);

  // This component doesn't render anything
  return null;
};
